import React, { useEffect, useState } from "react";
import firebase from "./config/fbConfig";
import { BrowserRouter, Switch, Route, useLocation, Redirect } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { isLoaded, useFirebase } from "react-redux-firebase";
import { AnimatePresence } from "framer-motion";
import { CircularProgress, Box, Typography, ThemeProvider, createTheme, CssBaseline, Button, Dialog, DialogContent, DialogActions } from '@mui/material';
// Helper to get branding
const getBranding = () => {
	try {
		const saved = localStorage.getItem('ellipse_branding');
		return saved ? JSON.parse(saved) : null;
	} catch (e) { return null; }
};
const branding = getBranding();

// Create standard theme
const theme = createTheme({
	typography: {
		fontFamily: "'Manrope', sans-serif",
		h1: { fontWeight: 800 },
		h2: { fontWeight: 700 },
		h3: { fontWeight: 700 },
		h4: { fontWeight: 600 },
		h5: { fontWeight: 600 },
		h6: { fontWeight: 600 },
		button: { textTransform: 'none', fontWeight: 600 },
	},
	palette: {
		primary: {
			main: branding?.primaryColor || '#92d1dc', // Dynamic Brand Blue
			contrastText: '#0f172a'
		},
		secondary: {
			main: branding?.secondaryColor || '#bfd56c', // Dynamic Brand Green
			contrastText: '#0f172a'
		},
		info: {
			main: branding?.tertiaryColor || '#f5d377', // New Brand Yellow
			contrastText: '#0f172a'
		},
	}
});

// Components (Lazy Loaded)
// Components (Lazy Loaded)
// Navigation is statically imported below to maintain shell stability.
// import Navigation from "./components/layout/Navigation"; 
// Keeping static shell for better UX (prevent shell flash)
import Navigation from "./components/layout/Navigation";
import ImpersonationBanner from "./components/UserManagement/ImpersonationBanner"; // [NEW]
const Dashboard = React.lazy(() => import("./components/dashboard/DashboardV2"));
const MyCoaches = React.lazy(() => import("./components/professors/MyCoaches"));
const Meetings = React.lazy(() => import("./components/meetings/Meetings"));
const Sprint = React.lazy(() => import("./components/sprint/EllipseSprintV3"));
const WaitingRoom = React.lazy(() => import("./components/sprint/WaitingRoom"));
const SpotifyCallback = React.lazy(() => import("./components/sprint/SpotifyCallback"));
const SprintAttendance = React.lazy(() => import("./components/sprint/SprintAttendance"));
const LearningLogs = React.lazy(() => import("./components/users/SearchUsers"));
const MyProgress = React.lazy(() => import("./components/users/MyProgress"));
const MyInvoices = React.lazy(() => import("./components/users/MyInvoices"));
const MyContracts = React.lazy(() => import("./components/users/MyContracts"));
const MyHistory = React.lazy(() => import("./components/users/MyHistory"));
const PrivacyPolicy = React.lazy(() => import("./components/PrivacyPolicy"));
const UserManagement = React.lazy(() => import("./components/UserManagement/UserManagementComponent"));
const Masterminds = React.lazy(() => import("./components/masterminds/mastermindsList"));
const MastermindSpace = React.lazy(() => import("./components/mastermind/MastermindSpace"));
const MastermindCoachConsole = React.lazy(() => import("./components/mastermind/MastermindCoachConsole"));
const Attendance = React.lazy(() => import("./components/attendanceRecord/AttendanceRecord"));
const Confidence = React.lazy(() => import("./components/confidenceApproche/confidenceApproche"));
const SprintAdmin = React.lazy(() => import("./components/sprintAdministration/SprintAdministrationComponent"));
const SprintPlanning = React.lazy(() => import("./components/SprintPlanning/sprintPlanning"));
const CompaniesAdmin = React.lazy(() => import("./components/companiesAdministration/CompaniesAdministrationComponent"));
const SignIn = React.lazy(() => import("./components/auth/SignIn"));
const SignUp = React.lazy(() => import("./components/auth/SignUp"));
const Onboarding = React.lazy(() => import("./components/auth/Onboarding"));
const MeetingDetails = React.lazy(() => import("./components/meetings/MeetingDetails"));
const Administration = React.lazy(() => import("./components/administration/administration"));
const SurveyIntact = React.lazy(() => import("./components/Survey/Intact"));
const Ressources = React.lazy(() => import("./components/Ressources/Ressources"));
const ContractGenerator = React.lazy(() => import("./components/administration/ContractGenerator"));
const AiCoachConfig = React.lazy(() => import("./components/administration/AiCoachConfig"));
const LeadConfig       = React.lazy(() => import("./components/administration/LeadConfig"));
const EmailPreviewPage = React.lazy(() => import("./components/administration/EmailPreviewPage"));
const MyTimesheet = React.lazy(() => import("./components/professors/MyTimesheet"));
const ArcadeHub = React.lazy(() => import("./components/Arcade/ArcadeHub"));
const VideoRoom = React.lazy(() => import("./components/meetings/VideoRoom"));
const PipedriveAssistant = React.lazy(() => import("./components/crm/PipedriveAssistant"));
const QuickBooksInvoiceAssistant = React.lazy(() => import("./components/administration/QuickBooksInvoiceAssistant"));
const BoutiqueConfig = React.lazy(() => import("./components/administration/BoutiqueConfig"));
const AdminScoreboard = React.lazy(() => import("./components/administration/AdminScoreboard"));
const SalesStatistics = React.lazy(() => import("./components/administration/SalesStatistics"));
const SalesStats = React.lazy(() => import("./components/administration/SalesStats"));
const CampaignDashboard = React.lazy(() => import("./pages/admin/Marketing/CampaignDashboard"));
const Integrations = React.lazy(() => import("./pages/admin/Marketing/Integrations"));
const AddLead = React.lazy(() => import("./pages/admin/Marketing/AddLead"));
const ScoringConfig = React.lazy(() => import("./pages/admin/Marketing/ScoringConfig"));
const WarmingLeads = React.lazy(() => import("./pages/admin/Marketing/WarmingLeads"));
const LinkedInProspects = React.lazy(() => import("./pages/admin/Marketing/LinkedInProspects"));
const GhlMigration = React.lazy(() => import("./pages/admin/Marketing/GhlMigration"));

const ClientIntelligence = React.lazy(() => import("./components/administration/ClientIntelligence"));
const LandingPageManager = React.lazy(() => import("./components/administration/LandingPageManager"));
const AdminSiteMap = React.lazy(() => import("./components/administration/AdminSiteMap"));
const BrochurePage = React.lazy(() => import("./components/public/BrochurePage"));
const AcademyCatalog = React.lazy(() => import("./components/academy/AcademyCatalog"));
const CourseDetail = React.lazy(() => import("./components/academy/CourseDetail"));
const LessonPlayer = React.lazy(() => import("./components/academy/LessonPlayer"));
const HomePage = React.lazy(() => import("./components/public/HomePage"));
const NarrativeFluent = React.lazy(() => import("./components/narrative/NarrativeFluent"));
const SprintTrialLP = React.lazy(() => import("./components/public/SprintTrialLP")); // [NEW]
const TrialActivation = React.lazy(() => import("./components/public/TrialActivation")); // [NEW]
const EnglishLevelQuiz = React.lazy(() => import("./components/public/EnglishLevelQuiz")); // [NEW]
const OpportunitesQuiz = React.lazy(() => import("./components/public/OpportunitesQuiz"));
const CommunicateurQuiz = React.lazy(() => import("./components/public/CommunicateurQuiz"));
const FreinQuiz = React.lazy(() => import("./components/public/FreinQuiz"));
const Schedule = React.lazy(() => import('./components/schedule/Schedule'));
const AIGenerativeForm = React.lazy(() => import("./components/forms/AIGenerativeForm")); // [NEW]
const Boutique = React.lazy(() => import("./components/boutique/Boutique"));
import EvaluationBlockingModal from "./components/auth/EvaluationBlockingModal";

import i18n from './i18n';
import "./App.css";

// Lazy Loader
const PageLoader = () => (
	<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" width="100%">
		<CircularProgress size={40} thickness={4} />
	</Box>
);

// Pages réservées à certains rôles. Une page absente de cette carte reste ouverte
// à tout utilisateur connecté (comportement existant préservé). Sinon, l'utilisateur
// doit avoir au moins un des rôles listés, sans quoi il est redirigé au dashboard.
const ROUTE_ROLES = {
	"/administration":           ["admin", "professor"],
	"/userManagement":           ["admin", "professor", "account manager"],
	"/sprintattendance":         ["admin", "professor", "account manager"],
	"/learningLogs":             ["admin", "professor"],
	"/my-timesheet":             ["admin", "professor"],
	"/sprintAdministration":     ["admin", "professor"],
	"/companiesAdministration":  ["admin", "professor"],
	"/sprintPlanning":           ["admin", "professor"],
	"/contractGenerator":        ["admin", "legal"],
	"/crm":                      ["admin"],
	"/quickbooks-assistant":     ["admin", "finance"],
	"/admin/sales-stats":        ["admin", "finance"],
	"/admin/statistics":         ["admin"],
	"/admin/intelligence":       ["admin"],
	"/admin/boutique-config":    ["admin"],
	"/admin/scoreboard":         ["admin"],
	"/admin/site-map":           ["admin"],
	"/admin/landing-pages":      ["admin"],
	"/admin/marketing":          ["admin"],
	"/admin/integrations":       ["admin"],
	"/admin/add-lead":           ["admin"],
	"/admin/linkedin-prospects": ["admin"],
	"/admin/scoring-config":     ["admin"],
	"/admin/warming-leads":      ["admin"],
};

// Helper to access location inside BrowserRouter
const AnimatedRoutes = () => {
	const location = useLocation();
	const profile = useSelector((s) => s.firebase.profile);

	// Garde de rôle. On n'applique la garde qu'une fois le profil chargé.
	if (isLoaded(profile) && !profile.isEmpty) {
		const roles = Array.isArray(profile.role) ? profile.role : (profile.role ? [profile.role] : []);
		// Un compte finance-only n'a pas de dashboard membre utile : sa page d'accueil est son outil.
		const isFinanceOnly = roles.includes("finance") && !roles.includes("admin") && !roles.includes("professor");
		const home = isFinanceOnly ? "/quickbooks-assistant" : "/";

		// finance-only au dashboard membre → on le ramène à son outil
		if (isFinanceOnly && location.pathname === "/") {
			return <Redirect to="/quickbooks-assistant" />;
		}
		// page réservée à des rôles que l'utilisateur n'a pas → redirection vers sa page d'accueil
		const required = ROUTE_ROLES[location.pathname];
		if (required && !roles.some((r) => required.includes(r))) {
			return <Redirect to={home} />;
		}
	}

	return (
		<AnimatePresence>
			<React.Suspense fallback={<PageLoader />}>
				<Switch location={location} key={location.pathname}>
					<Route exact path="/" component={Dashboard} />
					<Route exact path="/academy" component={AcademyCatalog} />
					<Route exact path="/academy/:courseId" component={CourseDetail} />
					<Route exact path="/academy/:courseId/:lessonId" component={LessonPlayer} />
					<Route exact path="/coaches" component={MyCoaches} />
					<Route exact path="/meetings" component={Meetings} />
					<Route exact path="/sprint" component={Sprint} />
					<Route exact path="/waiting/:sprintId" component={WaitingRoom} />
					<Route exact path="/spotify-callback" component={SpotifyCallback} />
					<Route exact path="/sprintattendance" component={SprintAttendance} />
					<Route exact path="/attendance" component={Attendance} />
					<Route exact path="/confidence" component={Confidence} />
					<Route exact path="/arcade" component={ArcadeHub} />
					<Route exact path="/intact" component={SurveyIntact} />
					<Route exact path="/userManagement" component={UserManagement} />
					<Route exact path="/userManagement" component={UserManagement} />
					<Route exact path="/resources" component={Ressources} />
					<Route exact path="/narrative" component={NarrativeFluent} />
					<Route exact path="/masterminds" component={Masterminds} />
					<Route exact path="/mastermind" component={MastermindSpace} />
					<Route exact path="/mastermind/coach" component={MastermindCoachConsole} />
					<Route exact path="/learningLogs" component={LearningLogs} />
					<Route exact path="/my-progress" component={MyProgress} />
					<Route exact path="/history" component={MyHistory} />
					<Route exact path="/invoices" component={MyInvoices} />
					<Route exact path="/contracts" component={MyContracts} />
					<Route exact path="/sprintAdministration" component={SprintAdmin} />
					<Route exact path="/companiesAdministration" component={CompaniesAdmin} />
					<Route exact path="/sprintPlanning" component={SprintPlanning} />
					<Route exact path="/contractGenerator" component={ContractGenerator} />
					<Route exact path="/administration" component={Administration} />
					<Route exact path="/ai-config" render={() => <Redirect to="/administration?tab=ai" />} />
					<Route exact path="/lead-config" render={() => <Redirect to="/administration?tab=ai" />} />
					<Route exact path="/email-preview" component={EmailPreviewPage} />
					<Route exact path="/my-timesheet" component={MyTimesheet} />
					<Route exact path="/meeting/:id" component={MeetingDetails} />
					<Route exact path="/room/:meetingId" component={VideoRoom} />
					<Route exact path="/crm" component={PipedriveAssistant} />
					<Route exact path="/quickbooks-assistant" component={QuickBooksInvoiceAssistant} />
					<Route exact path="/admin/boutique-config" component={BoutiqueConfig} />
					<Route exact path="/admin/scoreboard" component={AdminScoreboard} />
					<Route exact path="/admin/statistics" component={SalesStatistics} />
					<Route exact path="/admin/sales-stats" component={SalesStats} />
					<Route exact path="/admin/intelligence" component={ClientIntelligence} />
					<Route exact path="/admin/landing-pages" component={LandingPageManager} />
					<Route exact path="/admin/site-map" component={AdminSiteMap} />
					<Route exact path="/admin/marketing" component={CampaignDashboard} />
					<Route exact path="/admin/integrations" component={Integrations} />
					<Route exact path="/admin/add-lead" component={AddLead} />
					<Route exact path="/admin/linkedin-prospects" component={LinkedInProspects} />
					<Route exact path="/admin/scoring-config" component={ScoringConfig} />
					<Route exact path="/admin/warming-leads" component={WarmingLeads} />
					<Route exact path="/admin/ghl-migration" component={GhlMigration} />
					{/* Catch-all : toute URL inconnue renvoie au dashboard (évite la page blanche) */}
					<Route render={() => <Redirect to="/" />} />
				</Switch>
			</React.Suspense>
		</AnimatePresence>
	);
};

// Evaluation Modal Wrapper to react to route changes
const EvaluationWrapper = ({ profile, auth }) => {
	const location = useLocation();
	
	// Onboarding check: Done if explicitly true, OR if they have all critical fields (legacy)
	// We check for undefined to allow the legacy fields to take over if the flag hasn't been set yet.
	const onboardingDone = profile.onboardingCompleted === true || (
		(profile.onboardingCompleted === undefined || profile.onboardingCompleted === null) && 
		((profile.photoUrl || profile.avatarUrl) && profile.jobTitle && (profile.level || profile.Level))
	);

	const isTrial = profile.plan === 'TRIAL';
	const isStudent = !profile.role || profile.role === 'student' || (Array.isArray(profile.role) && profile.role.every(r => r === 'student'));
	const isMastermind = profile.packageType === "MASTERMIND";

	// Auto-fix : anciens membres sans evaluationCompleted → on le crée à true
	useEffect(() => {
		if (!isLoaded(profile) || profile.isEmpty || !auth.uid) return;
		if (Object.prototype.hasOwnProperty.call(profile, 'evaluationCompleted')) return;
		firebase.firestore().collection("users").doc(auth.uid).set({ evaluationCompleted: true }, { merge: true })
			.then(() => console.log("[App] evaluationCompleted auto-set to true for legacy member"))
			.catch(console.error);
	}, [auth.uid, profile]);

	const [hasLearningLog, setHasLearningLog] = useState(false);
	const [firstClassDismissed, setFirstClassDismissed] = useState(false);
	useEffect(() => {
		if (!auth.uid || !isMastermind) return;
		const unsub = firebase.firestore()
			.collection("learningLogs")
			.where("userId", "==", auth.uid)
			.onSnapshot(snap => {
				const hasValid = snap.docs.some(doc => (parseFloat(doc.data().duration) || 0) > 0);
				setHasLearningLog(hasValid);
			});
		return () => unsub();
	}, [auth.uid, isMastermind]);

	const isFrenchLearner = profile.learningLanguage === "fr";

	const openEval = isLoaded(profile) && !profile.isEmpty && auth.uid &&
		!location.pathname.includes('onboarding') &&
		profile.evaluationCompleted === false &&
		onboardingDone &&
		!isTrial &&
		isStudent &&
		!isFrenchLearner;

	const openFirstClass = isLoaded(profile) && !profile.isEmpty && auth.uid &&
		!location.pathname.includes('onboarding') &&
		isMastermind &&
		profile.evaluationCompleted === true &&
		!hasLearningLog &&
		!firstClassDismissed &&
		onboardingDone &&
		isStudent;

	console.log("[App] Onboarding status:", { onboardingCompleted: profile.onboardingCompleted, hasOnboardingProp: Object.prototype.hasOwnProperty.call(profile, 'onboardingCompleted'), onboardingDone });
	console.log("[App] Evaluation status:", { evaluationCompleted: profile.evaluationCompleted, openEval, openFirstClass });

	return (
		<>
			<EvaluationBlockingModal open={openEval} profile={profile} auth={auth} phase="eval" />
			<EvaluationBlockingModal open={openFirstClass} profile={profile} auth={auth} phase="first_class" onDismiss={() => setFirstClassDismissed(true)} />
		</>
	);
};

const App = ({ auth, profile }) => {
	const rfb = useFirebase();

	// Wait for Firestore profile when it loads as empty (Chrome offline race condition)
	const [profileTimedOut, setProfileTimedOut] = useState(false);
	useEffect(() => {
		if (!auth.uid || !isLoaded(profile) || !profile.isEmpty) {
			setProfileTimedOut(false);
			return;
		}
		const t = setTimeout(() => setProfileTimedOut(true), 5000);
		return () => clearTimeout(t);
	}, [auth.uid, profile]);

	// Inject Branding CSS Variables
	useEffect(() => {
		const root = document.documentElement;
		try {
			const saved = localStorage.getItem('ellipse_branding');
			const brand = saved ? JSON.parse(saved) : null;
			if (brand) {
				root.style.setProperty('--primary-main', brand.primaryColor || '#92d1dc');
				root.style.setProperty('--secondary-main', brand.secondaryColor || '#bfd56c');
				root.style.setProperty('--tertiary-main', brand.tertiaryColor || '#f5d377');
				root.style.setProperty('--button-main', brand.buttonColor || brand.secondaryColor || '#bfd56c');
			}
		} catch (e) {
			console.error("Error setting branding vars:", e);
		}
	}, []);

	// Sync Language
	useEffect(() => {
		if (isLoaded(profile) && profile.language) {
			i18n.changeLanguage(profile.language);
		}
	}, [profile]);

	// Loading State — also wait if profile is empty (Firestore not yet connected in Chrome)
	if (!isLoaded(profile) || !isLoaded(auth) || (auth.uid && isLoaded(profile) && profile.isEmpty && !profileTimedOut)) {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" textAlign="center">
				<Typography variant="body1" gutterBottom fontWeight={700}>
					Loading your Member Platform...
				</Typography>
				<Typography variant="body2" color="textSecondary" mb={3}>
					Paradise will be here in few seconds :D
				</Typography>
				<CircularProgress />
			</Box>
		);
	}

	const isBetaSite = window.location.hostname === 'ios-auth-test.web.app' || window.location.hostname === 'beta.groupellipse.com';
	const isAdmin = Array.isArray(profile.role) ? profile.role.includes('admin') : profile.role === 'admin';
	// Migration terminée : la beta sert de nouveau à tester les prochaines versions.
	// Remettre à true pour réactiver la redirection des membres vers la prod.
	const MIGRATION_REDIRECT_ENABLED = false;
	const showBetaBanner = MIGRATION_REDIRECT_ENABLED && isBetaSite && !!auth.uid && !isAdmin;


	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<ImpersonationBanner />
			<Dialog open={showBetaBanner} disableEscapeKeyDown onClose={() => {}} BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15,23,42,0.85)' } }} PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', maxWidth: 460 } }}>
				<Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', px: 4, pt: 4, pb: 3, textAlign: 'center' }}>
					<Typography variant="h2" sx={{ fontSize: 40, mb: 1 }}>🎉</Typography>
					<Typography variant="h5" fontWeight={800} sx={{ color: 'white', mb: 1 }}>
						Le nouveau portail est arrivé !
					</Typography>
					<Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
						Déployé le 22 mai 2026
					</Typography>
				</Box>
				<DialogContent sx={{ textAlign: 'center', px: 4, pt: 3 }}>
					<Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
						Ton espace membre Ellipse a été entièrement repensé et est maintenant accessible à l'adresse officielle.
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Retrouve toutes tes sessions, ton coach et tes ressources sur :
					</Typography>
					<Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1, color: '#0f172a' }}>
						members.groupellipse.com
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center', pb: 4, px: 4 }}>
					<Button
						variant="contained"
						size="large"
						fullWidth
						onClick={async () => {
							try { await firebase.auth().signOut(); } catch(e) {}
							window.location.replace('https://members.groupellipse.com');
						}}
						sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1rem', bgcolor: '#92d1dc', color: '#0f172a', '&:hover': { bgcolor: '#7bbfca' } }}
					>
						Accéder au nouveau portail →
					</Button>
				</DialogActions>
			</Dialog>
			<BrowserRouter>
				<EvaluationWrapper profile={profile} auth={auth} />
				<React.Suspense fallback={<PageLoader />}>
					{/* Route Structure:
						If /signin or /signup => Render Auth pages (No Navigation)
						If /mobile => Render Dedicated Mobile App Layout
						Else => Render Legacy Web App with Navigation
					*/}
					<Switch>
						<Route path="/signin" component={SignIn} />
						<Route path="/signup" component={SignUp} />

						{/* Public Website - Dev Only */}
						{process.env.NODE_ENV === 'development' && <Route path="/home" component={HomePage} />}

						{/* Public Brochure & Privacy Policies */}
						<Route path="/peopledesk" component={React.lazy(() => import("./components/public/PeopleDeskBrochure"))} />
						<Route path="/brochure" component={BrochurePage} />
						<Route path="/privacy" component={PrivacyPolicy} />

						{/* Public Boutique */}
						<Route exact path="/boutique">
							{!auth.uid ? (
								<Boutique />
							) : (
								<Navigation auth={auth} profile={profile}>
									<Boutique />
								</Navigation>
							)}
						</Route>

						{/* Public Room Access (Guests + Auth) */}
						<Route path="/room/:meetingId" component={VideoRoom} />

						{/* Public Sprint Trial Registration */}
						<Route path="/sprint-trial" component={SprintTrialLP} />
						<Route path="/activate-trial" component={TrialActivation} />

						{/* Public Quiz de niveau anglais */}
						<Route path="/quiz/opportunites" component={OpportunitesQuiz} />
						<Route path="/quiz/communicateur" component={CommunicateurQuiz} />
						<Route path="/quiz/frein" component={FreinQuiz} />
						<Route exact path="/quiz" component={EnglishLevelQuiz} />

						{/* Public AI Demo Form */}
						<Route path="/ai-form" component={AIGenerativeForm} />

						{/* Public Contract Signing */}
						<Route path="/sign/:contractId" component={React.lazy(() => import("./components/public/ContractSignPage"))} />

						{/* App Pages */}
						<Route path="/schedule" component={Schedule} />

						{/* ONBOARDING FLOW */}
						<Route path="/onboarding">
							{!auth.uid ? <Redirect to="/signin" /> :
								(!!profile.onboardingCompleted || !!sessionStorage.getItem('surrogate_uid') ?
									<Redirect to="/" /> :
									<Onboarding auth={auth} profile={profile} />
								)
							}
						</Route>

						{/* Default / Protected Area (Legacy Web) */}
						<Route path="/">
							{!auth.uid ? (
								<Redirect to="/signin" />
							) : (
								(() => {
									const rawVal = profile.onboardingCompleted;
									const hasProp = Object.prototype.hasOwnProperty.call(profile, 'onboardingCompleted');
									const hasFields = !!((profile.photoUrl || profile.avatarUrl) && profile.jobTitle && (profile.level || profile.Level));
									const isDone = rawVal === true || (!hasProp && hasFields);
									const shouldRedirect = isLoaded(profile) && !profile.isEmpty && !profile.role?.includes('admin') && !sessionStorage.getItem('surrogate_uid') && !isDone && profile.plan !== 'TRIAL';
									console.log("[App] Redirect check detail:", { rawVal, hasProp, hasFields, isDone, shouldRedirect, path: window.location.pathname });
									return shouldRedirect;
								})() ? (
									<Redirect to="/onboarding" />
								) : (
									<Navigation auth={auth} profile={profile}>
										<AnimatedRoutes />
									</Navigation>
								)
							)}
						</Route>
					</Switch>
				</React.Suspense>
			</BrowserRouter>
		</ThemeProvider>
	);
};

const mapStateToProps = (state) => ({
	auth: state.firebase.auth,
	profile: state.firebase.profile,
});

export default connect(mapStateToProps)(App);
